import { Component } from '@theme/component';

/** @typedef {{ length: string; uk: string; eur: string; us: string; jp: string }} FootwearSizeRow */

/** @typedef {'cm' | 'inch'} FootwearSizeUnit */

/** @type {FootwearSizeRow[]} */
const FOOTWEAR_SIZE_ROWS_CM = [
  { length: '24,5', uk: '6.5', eur: '40', us: '7 M', jp: '25' },
  { length: '25', uk: '7', eur: '40 2/3', us: '7.5 M', jp: '25,5' },
  { length: '25,5', uk: '7.5', eur: '41 1/3', us: '8 M', jp: '26' },
  { length: '26', uk: '8', eur: '42', us: '8.5 M', jp: '26,5' },
  { length: '26,5', uk: '8.5', eur: '42 2/3', us: '9 M', jp: '27' },
  { length: '27', uk: '9', eur: '43 1/3', us: '9.5 M', jp: '27,5' },
  { length: '27,5', uk: '9.5', eur: '44', us: '10 M', jp: '28' },
  { length: '28', uk: '10', eur: '44 2/3', us: '10.5 M', jp: '28,5' },
  { length: '28,5', uk: '10.5', eur: '45 1/3', us: '11 M', jp: '29' },
  { length: '29', uk: '11', eur: '46', us: '11.5 M', jp: '29,5' },
  { length: '29,5', uk: '11.5', eur: '46 2/3', us: '12 M', jp: '30' },
  { length: '30', uk: '12', eur: '47 1/3', us: '12.5 M', jp: '30,5' },
  { length: '30,5', uk: '12.5', eur: '48', us: '13 M', jp: '31' },
  { length: '31', uk: '13', eur: '48 2/3', us: '13.5 M', jp: '31,5' },
  { length: '31,5', uk: '13.5', eur: '49 1/3', us: '14 M', jp: '32' },
];

/** @type {FootwearSizeRow[]} */
const FOOTWEAR_SIZE_ROWS_INCH = [
  { length: '9,6', uk: '6.5', eur: '40', us: '7 M', jp: '25' },
  { length: '9,8', uk: '7', eur: '40 2/3', us: '7.5 M', jp: '25,5' },
  { length: '10', uk: '7.5', eur: '41 1/3', us: '8 M', jp: '26' },
  { length: '10,2', uk: '8', eur: '42', us: '8.5 M', jp: '26,5' },
  { length: '10,4', uk: '8.5', eur: '42 2/3', us: '9 M', jp: '27' },
  { length: '10,6', uk: '9', eur: '43 1/3', us: '9.5 M', jp: '27,5' },
  { length: '10,8', uk: '9.5', eur: '44', us: '10 M', jp: '28' },
  { length: '11', uk: '10', eur: '44 2/3', us: '10.5 M', jp: '28,5' },
  { length: '11,2', uk: '10.5', eur: '45 1/3', us: '11 M', jp: '29' },
  { length: '11,4', uk: '11', eur: '46', us: '11.5 M', jp: '29,5' },
  { length: '11,6', uk: '11.5', eur: '46 2/3', us: '12 M', jp: '30' },
  { length: '11,8', uk: '12', eur: '47 1/3', us: '12.5 M', jp: '30,5' },
  { length: '12', uk: '12.5', eur: '48', us: '13 M', jp: '31' },
  { length: '12,2', uk: '13', eur: '48 2/3', us: '13.5 M', jp: '31,5' },
  { length: '12,4', uk: '13.5', eur: '49 1/3', us: '14 M', jp: '32' },
];

const FOOTWEAR_SIZE_DEFAULT_LENGTH_CM = '27,5';
const FOOTWEAR_SIZE_DEFAULT_LENGTH_INCH = '10,8';

/**
 * @typedef {object} Refs
 * @property {HTMLSelectElement} lengthSelect
 * @property {HTMLElement} sizeResult
 * @property {HTMLTableSectionElement} tableBody
 * @property {HTMLTableCellElement} lengthHeader
 * @property {HTMLButtonElement} unitCm
 * @property {HTMLButtonElement} unitInch
 * @property {HTMLLabelElement} lengthLabel
 */

/** @extends {Component<Refs>} */
class FootwearSizeGuide extends Component {
  /** @type {FootwearSizeUnit} */
  #unit = 'cm';

  connectedCallback() {
    super.connectedCallback();
    this.#setUnit('cm');
  }

  /**
   * @param {Event} event
   */
  setUnitCm(event) {
    event.preventDefault();
    this.#setUnit('cm');
  }

  /**
   * @param {Event} event
   */
  setUnitInch(event) {
    event.preventDefault();
    this.#setUnit('inch');
  }

  onLengthChange() {
    this.#updateSizeResult();
  }

  /** @param {FootwearSizeUnit} unit */
  #setUnit(unit) {
    this.#unit = unit;
    const rows = unit === 'cm' ? FOOTWEAR_SIZE_ROWS_CM : FOOTWEAR_SIZE_ROWS_INCH;
    const { lengthSelect, lengthHeader, lengthLabel, unitCm, unitInch, tableBody } = this.refs;

    if (!lengthSelect || !tableBody || !lengthHeader) return;

    lengthHeader.textContent =
      unit === 'cm' ? 'LONGITUD DEL PIE (CM)' : 'LONGITUD DEL PIE (INCH)';
    lengthLabel.textContent =
      unit === 'cm' ? 'Longitud del pie (cm)' : 'Longitud del pie (inch)';

    unitCm.setAttribute('aria-pressed', unit === 'cm' ? 'true' : 'false');
    unitInch.setAttribute('aria-pressed', unit === 'inch' ? 'true' : 'false');
    unitCm.classList.toggle('is-active', unit === 'cm');
    unitInch.classList.toggle('is-active', unit === 'inch');

    const previousLength = lengthSelect.value;
    lengthSelect.replaceChildren(
      ...rows.map((row) => {
        const option = document.createElement('option');
        option.value = row.length;
        option.textContent = row.length;
        return option;
      })
    );

    const defaultLength =
      unit === 'cm' ? FOOTWEAR_SIZE_DEFAULT_LENGTH_CM : FOOTWEAR_SIZE_DEFAULT_LENGTH_INCH;
    const hasPrevious = rows.some((row) => row.length === previousLength);
    lengthSelect.value = hasPrevious ? previousLength : defaultLength;

    this.#renderTable(rows);
    this.#updateSizeResult();
  }

  /** @param {FootwearSizeRow[]} rows */
  #renderTable(rows) {
    const { tableBody } = this.refs;
    if (!tableBody) return;

    tableBody.replaceChildren(
      ...rows.map((row, index) => {
        const tr = document.createElement('tr');
        if (index % 2 === 1) tr.classList.add('footwear-size-guide__row--alt');
        tr.innerHTML = `
          <td>${row.length}</td>
          <td>${row.uk}</td>
          <td>${row.eur}</td>
          <td>${row.us}</td>
          <td>${row.jp}</td>
        `;
        return tr;
      })
    );
  }

  #updateSizeResult() {
    const { lengthSelect, sizeResult } = this.refs;
    if (!lengthSelect || !sizeResult) return;

    const rows = this.#unit === 'cm' ? FOOTWEAR_SIZE_ROWS_CM : FOOTWEAR_SIZE_ROWS_INCH;
    const match = rows.find((row) => row.length === lengthSelect.value);
    sizeResult.textContent = match?.eur ?? '—';
  }
}

if (!customElements.get('footwear-size-guide')) {
  customElements.define('footwear-size-guide', FootwearSizeGuide);
}
